import { getManilaCalendarYmd } from "./dateTime";

export const EVENT_ARCHIVE_GRACE_MS = 24 * 60 * 60 * 1000;
const DATE_ONLY_PATTERN = /^(\d{4})-\d{2}-\d{2}$/;
const TIME_PATTERN = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/;
const AUTO_ARCHIVE_STATUSES = new Set(["published", "upcoming", "past"]);
const YMD_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export const parseEventDateTime = (event) => {
  if (!event?.date) return null;

  const datePart = String(event.date).trim();
  const timePart = String(event.time || "00:00").trim();
  const parsed = new Date(`${datePart}T${timePart}`);

  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const toEventDatePart = (value) => {
  const datePart = String(value || "").trim().slice(0, 10);
  return DATE_ONLY_PATTERN.test(datePart) ? datePart : "";
};

export const getEventEndMs = (event) => {
  const datePart = toEventDatePart(event?.date);
  if (!datePart) return null;

  const timeMatch = TIME_PATTERN.exec(String(event?.time || "00:00").trim());
  const hours = String(timeMatch ? Number(timeMatch[1]) : 0).padStart(2, "0");
  const minutes = String(timeMatch ? timeMatch[2] : "00");
  const seconds = String(timeMatch && timeMatch[3] ? timeMatch[3] : "00");
  const parsed = new Date(`${datePart}T${hours}:${minutes}:${seconds}+08:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.getTime();
};

export const getEventArchiveAtMs = (event) => {
  const endMs = getEventEndMs(event);
  return endMs == null ? null : endMs + EVENT_ARCHIVE_GRACE_MS;
};

export const shouldAutoArchive = (event, now = Date.now()) => {
  const savedStatus = String(event?.status || "").toLowerCase();
  if (savedStatus === "archived" || savedStatus === "draft") return false;
  if (!AUTO_ARCHIVE_STATUSES.has(savedStatus)) return false;

  const archiveAt = getEventArchiveAtMs(event);
  if (archiveAt == null) return false;
  return now >= archiveAt;
};

export const archiveEventsPastGracePeriod = async (supabase, events = [], now = Date.now()) => {
  const ids = (Array.isArray(events) ? events : [])
    .filter((event) => event?.id && shouldAutoArchive(event, now))
    .map((event) => event.id);

  if (!ids.length || !supabase) return [];

  const { data, error } = await supabase
    .from("events")
    .update({ status: "archived" })
    .in("id", ids)
    .select("id");

  if (error) {
    console.error("Failed to auto-archive ended events:", error);
    return [];
  }

  return (data || []).map((row) => row.id);
};

const addCalendarDays = (ymd, days) => {
  const match = YMD_PATTERN.exec(String(ymd || "").trim());
  if (!match) return "";
  const utcDate = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + Number(days || 0))
  );
  const year = utcDate.getUTCFullYear();
  const month = String(utcDate.getUTCMonth() + 1).padStart(2, "0");
  const day = String(utcDate.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getManilaWeekEndYmd = (now = Date.now()) => {
  const today = getManilaCalendarYmd(now);
  const match = YMD_PATTERN.exec(today);
  if (!match) return "";
  const weekday = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))).getUTCDay();
  const daysUntilSunday = weekday === 0 ? 0 : 7 - weekday;
  return addCalendarDays(today, daysUntilSunday);
};

export const getEventStartMs = (event) => getEventEndMs(event);

const isLivePublishedStatus = (savedStatus) =>
  savedStatus === "published" || savedStatus === "upcoming";

export const getComputedEventStatus = (event, now = Date.now()) => {
  const savedStatus = String(event?.status || "").toLowerCase();
  const eventDate = parseEventDateTime(event);
  const startMs = getEventStartMs(event);
  const isPastByDate = eventDate ? eventDate.getTime() < now : false;
  const eventYmd = toEventDatePart(event?.date);
  const todayYmd = getManilaCalendarYmd(now);
  const isOngoingToday =
    isLivePublishedStatus(savedStatus) &&
    Boolean(eventYmd && todayYmd && eventYmd === todayYmd) &&
    startMs != null &&
    startMs <= now &&
    !shouldAutoArchive(event, now);

  if (savedStatus === "archived") return "archived";
  if (savedStatus === "draft") return "draft";
  if (savedStatus === "past") return "past";
  if (isOngoingToday) return "published";
  if (isPastByDate) return "past";

  if (isLivePublishedStatus(savedStatus)) {
    return "published";
  }

  return "draft";
};

export const isPublicTicketEvent = (event, now = Date.now()) =>
  getComputedEventStatus(event, now) === "published";

export const isUpcomingPublicEvent = (event, now = Date.now()) => {
  if (!isPublicTicketEvent(event, now)) return false;
  const startMs = getEventStartMs(event);
  return startMs == null || startMs > now;
};

export const isOngoingPublicEvent = (event, now = Date.now()) => {
  if (!isPublicTicketEvent(event, now)) return false;
  const startMs = getEventStartMs(event);
  const eventYmd = toEventDatePart(event?.date);
  const todayYmd = getManilaCalendarYmd(now);
  return Boolean(eventYmd && todayYmd && eventYmd === todayYmd && startMs != null && startMs <= now);
};

export const isEventInManilaWeek = (event, now = Date.now()) => {
  const eventYmd = toEventDatePart(event?.date);
  const todayYmd = getManilaCalendarYmd(now);
  const weekEndYmd = getManilaWeekEndYmd(now);
  return Boolean(eventYmd && todayYmd && weekEndYmd && eventYmd >= todayYmd && eventYmd <= weekEndYmd);
};

export const isEventInManilaMonth = (event, now = Date.now()) => {
  const eventYmd = toEventDatePart(event?.date);
  const todayYmd = getManilaCalendarYmd(now);
  return Boolean(
    eventYmd &&
      todayYmd &&
      eventYmd >= todayYmd &&
      eventYmd.slice(0, 7) === todayYmd.slice(0, 7)
  );
};

export const filterPublicTicketEvents = (events = [], now = Date.now()) =>
  (Array.isArray(events) ? events : []).filter((event) => isPublicTicketEvent(event, now));

export const filterEventsForPublicPage = (
  events = [],
  {
    status = "All",
    venue = "",
    availability = "All",
    upcomingRange = "all",
    soldOutByEventId = {},
    now = Date.now(),
  } = {}
) => (
  (Array.isArray(events) ? events : []).filter((event) => {
    if (!isPublicTicketEvent(event, now)) return false;
    if (status === "Upcoming" && !isUpcomingPublicEvent(event, now)) return false;
    if (status === "Ongoing" && !isOngoingPublicEvent(event, now)) return false;
    if (status === "Upcoming" && upcomingRange === "week" && !isEventInManilaWeek(event, now)) return false;
    if (status === "Upcoming" && upcomingRange === "month" && !isEventInManilaMonth(event, now)) return false;
    if (venue && String(event.venue || "").trim() !== venue) return false;

    const soldOut = Boolean(soldOutByEventId[String(event.id)]);
    if (availability === "Available" && soldOut) return false;
    if (availability === "Sold out" && !soldOut) return false;
    return true;
  })
);

export const getPublicEventsEmptyMessage = ({
  status = "All",
  venue = "",
  availability = "All",
  upcomingRange = "all",
  hasActiveSearch = false,
  searchNoResultsMessage = "No events match your search.",
} = {}) => {
  if (hasActiveSearch) return searchNoResultsMessage;
  if (status === "Ongoing") return "No events happening today.";

  const parts = [];
  if (status === "Upcoming") {
    if (upcomingRange === "week") parts.push("this week");
    else if (upcomingRange === "month") parts.push("this month");
    else parts.push("upcoming");
  }
  if (venue) parts.push(`at ${venue}`);
  if (availability === "Available") parts.push("with tickets available");
  if (availability === "Sold out") parts.push("that are sold out");

  if (!parts.length) return "No events available at this time.";
  return `No events ${parts.join(" ")}.`;
};
